import { SerializedConstructor } from '@langchain/core/load/serializable';

import { BuiltinTool } from '@/utils/ai-tools';

import { DEFAULT_MODEL as DEFAULT_BEDROCK_CHAT_MODEL } from './components/manifest/serialized/chat_models/ManifestBedrockChat.utils';
import { DEFAULT_MODEL as DEFAULT_ANTHROPIC_MODEL } from './components/manifest/serialized/chat_models/ManifestChatAnthropic.utils';
import { DEFAULT_MODEL as DEFAULT_CHAT_DEEPSEEK_MODEL } from './components/manifest/serialized/chat_models/ManifestChatDeepSeek.utils';
import { DEFAULT_MODEL as DEFAULT_GOOGLE_GENAI_MODEL } from './components/manifest/serialized/chat_models/ManifestChatGoogleGenerativeAI.utils';
import { DEFAULT_MODEL as DEFAULT_VERTEX_AI_MODEL } from './components/manifest/serialized/chat_models/ManifestChatGoogleVertexAI.utils';
import { DEFAULT_MODEL as DEFAULT_CHAT_VERTEX_AI_MODEL } from './components/manifest/serialized/chat_models/ManifestChatGoogleVertexAI.utils';
import { DEFAULT_MODEL as DEFAULT_CHAT_GROQ_MODEL } from './components/manifest/serialized/chat_models/ManifestChatGroq.utils';
import { DEFAULT_MODEL as DEFAULT_CHAT_MISTRAL_AI_MODEL } from './components/manifest/serialized/chat_models/ManifestChatMistralAI.utils';
import { DEFAULT_MODEL as DEFAULT_OPENAI_MODEL } from './components/manifest/serialized/chat_models/ManifestChatOpenAI.utils';
import { DEFAULT_MODEL as DEFAULT_CHAT_XAI_MODEL } from './components/manifest/serialized/chat_models/ManifestChatXAI.utils';
import { DEFAULT_MODEL as DEFAULT_BEDROCK_MODEL } from './components/manifest/serialized/llms/ManifestBedrock.utils';

const DISPLAY_NAME_MAPPING = {
  OpenAI: 'OpenAI',
  AzureOpenAI: 'Azure OpenAI',
  Fireworks: 'Fireworks',
  VertexAI: 'Google Vertex AI',
  Bedrock: 'Amazon Bedrock',
  Anthropic: 'Anthropic',
  GoogleGenerativeAI: 'Google Gemini',
  MistralAI: 'Mistral AI',
  Groq: 'Groq',
  DeepSeek: 'DeepSeek',
  CustomModel: 'Custom Model',
  CustomModelEndpoint: 'OpenAI Compatible Endpoint',
  XAI: 'XAI',
};

type ModelIdAndDisplayName = {
  modelId: string;
  displayName: string;
};

export const TEXT_MODEL_MAPPING: Record<string, ModelIdAndDisplayName> = {
  OpenAI: {
    modelId: 'langchain/llms/openai/OpenAI',
    displayName: DISPLAY_NAME_MAPPING.OpenAI,
  },
  AzureOpenAI: {
    modelId: 'langchain/llms/openai/AzureOpenAI',
    displayName: DISPLAY_NAME_MAPPING.AzureOpenAI,
  },
  Fireworks: {
    modelId: 'langchain/llms/fireworks/Fireworks',
    displayName: DISPLAY_NAME_MAPPING.Fireworks,
  },
  VertexAI: {
    modelId: 'langchain/llms/googlellm/VertexAI',
    displayName: DISPLAY_NAME_MAPPING.VertexAI,
  },
  Bedrock: {
    modelId: 'langchain/llms/bedrock/Bedrock',
    displayName: DISPLAY_NAME_MAPPING.Bedrock,
  },
  CustomModel: {
    modelId: 'langsmith/playground/CustomModel',
    displayName: DISPLAY_NAME_MAPPING.CustomModel,
  },
};

export const DEFAULT_TEXT_MODEL = TEXT_MODEL_MAPPING.OpenAI;

export const CHAT_MODEL_MAPPING: Record<string, ModelIdAndDisplayName> = {
  ChatOpenAI: {
    modelId: 'langchain/chat_models/openai/ChatOpenAI',
    displayName: DISPLAY_NAME_MAPPING.OpenAI,
  },
  CustomModelEndpoint: {
    modelId: 'langsmith/playground/CustomModelEndpoint',
    displayName: DISPLAY_NAME_MAPPING.CustomModelEndpoint,
  },
  AzureChatOpenAI: {
    modelId: 'langchain/chat_models/azure_openai/AzureChatOpenAI',
    displayName: DISPLAY_NAME_MAPPING.AzureOpenAI,
  },
  ChatAnthropic: {
    modelId: 'langchain/chat_models/anthropic/ChatAnthropic',
    displayName: DISPLAY_NAME_MAPPING.Anthropic,
  },
  ChatGoogleGenerativeAI: {
    modelId: 'langchain_google_genai/chat_models/ChatGoogleGenerativeAI',
    displayName: DISPLAY_NAME_MAPPING.GoogleGenerativeAI,
  },
  ChatVertexAI: {
    modelId: 'langchain/chat_models/vertexai/ChatVertexAI',
    displayName: DISPLAY_NAME_MAPPING.VertexAI,
  },
  ChatBedrockConverse: {
    modelId: 'langchain_aws/chat_models/ChatBedrockConverse',
    displayName: DISPLAY_NAME_MAPPING.Bedrock,
  },
  ChatBedrock: {
    modelId: 'langchain/chat_models/bedrock/ChatBedrock',
    displayName: DISPLAY_NAME_MAPPING.Bedrock,
  },
  ChatMistralAI: {
    modelId: 'langchain/chat_models/mistralai/ChatMistralAI',
    displayName: DISPLAY_NAME_MAPPING.MistralAI,
  },
  ChatFireworks: {
    modelId: 'langchain/chat_models/fireworks/ChatFireworks',
    displayName: DISPLAY_NAME_MAPPING.Fireworks,
  },
  ChatGroq: {
    modelId: 'langchain_groq/chat_models/ChatGroq',
    displayName: DISPLAY_NAME_MAPPING.Groq,
  },
  ChatDeepSeek: {
    modelId: 'langchain_deepseek/chat_models/ChatDeepSeek',
    displayName: DISPLAY_NAME_MAPPING.DeepSeek,
  },
  ChatCustomModel: {
    modelId: 'langsmith/playground/ChatCustomModel',
    displayName: DISPLAY_NAME_MAPPING.CustomModel,
  },
  ChatXAI: {
    modelId: 'langchain_xai/chat_models/ChatXAI',
    displayName: DISPLAY_NAME_MAPPING.XAI,
  },
};

export const DEFAULT_CHAT_MODEL = CHAT_MODEL_MAPPING.ChatOpenAI;

export const STRUCTURED_MODELS: ModelIdAndDisplayName[] = [
  {
    modelId: 'langchain/chat_models/openai/ChatOpenAI',
    displayName: DISPLAY_NAME_MAPPING.OpenAI,
  },
  {
    modelId: 'langchain/chat_models/azure_openai/AzureChatOpenAI',
    displayName: DISPLAY_NAME_MAPPING.AzureOpenAI,
  },
  {
    modelId: 'langchain/chat_models/anthropic/ChatAnthropic',
    displayName: DISPLAY_NAME_MAPPING.Anthropic,
  },
  {
    modelId: 'langchain/chat_models/vertexai/ChatVertexAI',
    displayName: DISPLAY_NAME_MAPPING.VertexAI,
  },
  {
    modelId: 'langchain_google_genai/chat_models/ChatGoogleGenerativeAI',
    displayName: DISPLAY_NAME_MAPPING.GoogleGenerativeAI,
  },
  {
    modelId: 'langchain/chat_models/fireworks/ChatFireworks',
    displayName: DISPLAY_NAME_MAPPING.Fireworks,
  },
  {
    modelId: 'langchain_aws/chat_models/ChatBedrockConverse',
    displayName: DISPLAY_NAME_MAPPING.Bedrock,
  },
  {
    modelId: 'langchain_groq/chat_models/ChatGroq',
    displayName: DISPLAY_NAME_MAPPING.Groq,
  },
  {
    modelId: 'langchain_xai/chat_models/ChatXAI',
    displayName: DISPLAY_NAME_MAPPING.XAI,
  },
  {
    modelId: 'langchain_deepseek/chat_models/ChatDeepSeek',
    displayName: DISPLAY_NAME_MAPPING.DeepSeek,
  },
];

export function getModelDisplayName(modelId: string): string {
  const parts = modelId.split('/');
  const providerId = parts[parts.length - 1];
  if (providerId in CHAT_MODEL_MAPPING) {
    return CHAT_MODEL_MAPPING[providerId].displayName;
  }
  return providerId || '';
}

export function getModelDisplayNameFromIdList(idList: string[]): string {
  return getModelDisplayName(idList.join('/'));
}

export const OPTIONAL_MODEL_SECRETS = ['AWS_SESSION_TOKEN'];

export const MODEL_SECRETS = {
  'langchain/llms/openai/OpenAI': { openai_api_key: 'OPENAI_API_KEY' },
  'langchain/llms/openai/AzureOpenAI': {
    openai_api_key: 'AZURE_OPENAI_API_KEY',
  },
  'langchain/chat_models/azure_openai/AzureChatOpenAI': {
    openai_api_key: 'AZURE_OPENAI_API_KEY',
  },
  'langchain/chat_models/openai/ChatOpenAI': {
    openai_api_key: 'OPENAI_API_KEY',
  },
  'langsmith/playground/CustomModelEndpoint': {
    openai_api_key: 'OPENAI_API_KEY',
  },
  'langchain/chat_models/anthropic/ChatAnthropic': {
    anthropic_api_key: 'ANTHROPIC_API_KEY',
  },
  'langchain/llms/googlellm/VertexAI': {
    'authOptions.credentials': 'GOOGLE_VERTEX_AI_WEB_CREDENTIALS',
  },
  'langchain/chat_models/google_genai/ChatGoogleGenerativeAI': {
    google_api_key: 'GOOGLE_API_KEY',
  },
  'langchain/chat_models/mistral_ai/ChatMistralAI': {
    mistral_api_key: 'MISTRAL_API_KEY',
  },
  'langchain/chat_models/chat_integration/ChatVertexAI': {
    'authOptions.credentials': 'GOOGLE_VERTEX_AI_WEB_CREDENTIALS',
  },
  'langchain/llms/fireworks/Fireworks': {
    fireworks_api_key: 'FIREWORKS_API_KEY',
  },
  'langchain/chat_models/fireworks/ChatFireworks': {
    fireworks_api_key: 'FIREWORKS_API_KEY',
  },
  'langchain/llms/bedrock/Bedrock': {
    aws_access_key_id: 'AWS_ACCESS_KEY_ID',
    aws_secret_access_key: 'AWS_SECRET_ACCESS_KEY',
    aws_session_token: 'AWS_SESSION_TOKEN',
  },
  'langchain/chat_models/bedrock/BedrockChat': {
    aws_access_key_id: 'AWS_ACCESS_KEY_ID',
    aws_secret_access_key: 'AWS_SECRET_ACCESS_KEY',
    aws_session_token: 'AWS_SESSION_TOKEN',
  },
  'langsmith/playground/ChatCustomModel': {},
  'langsmith/playground/CustomModel': {},
  'langchain/chat_models/groq/ChatGroq': {
    groq_api_key: 'GROQ_API_KEY',
  },
  'langchain/chat_models/vertexai/ChatVertexAI': {
    credentials: 'GOOGLE_VERTEX_AI_WEB_CREDENTIALS',
  },
  'langchain/chat_models/mistralai/ChatMistralAI': {
    mistral_api_key: 'MISTRAL_API_KEY',
  },
  'langchain_google_genai/chat_models/ChatGoogleGenerativeAI': {
    google_api_key: 'GOOGLE_API_KEY',
  },
  'langchain/chat_models/bedrock/ChatBedrock': {
    aws_access_key_id: 'AWS_ACCESS_KEY_ID',
    aws_secret_access_key: 'AWS_SECRET_ACCESS_KEY',
    aws_session_token: 'AWS_SESSION_TOKEN',
  },
  'langchain_aws/chat_models/ChatBedrockConverse': {
    aws_access_key_id: 'AWS_ACCESS_KEY_ID',
    aws_secret_access_key: 'AWS_SECRET_ACCESS_KEY',
    aws_session_token: 'AWS_SESSION_TOKEN',
  },
  'langchain_groq/chat_models/ChatGroq': {
    groq_api_key: 'GROQ_API_KEY',
  },
  'langchain_xai/chat_models/ChatXAI': {
    api_key: 'XAI_API_KEY',
  },
  'langchain_deepseek/chat_models/ChatDeepSeek': {
    api_key: 'DEEPSEEK_API_KEY',
  },
};

// These are the values used in the UI components when switching between providers
// The default values are the values that will be recognized by the provider in the model config UI
// Anything else present in the manifest will go into the Extra Params section
export const MODEL_DEFAULT_KWARGS = {
  'langchain/llms/openai/OpenAI': {
    temperature: 1.0,
    max_tokens: undefined,
    top_p: 1.0,
    presence_penalty: null,
    frequency_penalty: null,
    model: DEFAULT_OPENAI_MODEL,
    seed: undefined,
  },
  'langchain/llms/openai/AzureOpenAI': {
    temperature: 1.0,
    max_tokens: undefined,
    top_p: 1.0,
    presence_penalty: null,
    frequency_penalty: null,
    deployment_name: '',
    azure_endpoint: '',
    openai_api_version: undefined,
    seed: undefined,
  },
  'langchain/chat_models/azure_openai/AzureChatOpenAI': {
    temperature: 1.0,
    max_tokens: undefined,
    top_p: 1.0,
    presence_penalty: null,
    frequency_penalty: null,
    max_completion_tokens: undefined,
    extra_headers: {},
    deployment_name: '',
    azure_endpoint: '',
    openai_api_version: undefined,
    response_format: undefined,
    seed: undefined,
  },
  'langchain/chat_models/openai/ChatOpenAI': {
    temperature: 1.0,
    max_tokens: undefined,
    top_p: 1.0,
    presence_penalty: null,
    frequency_penalty: null,
    model: DEFAULT_OPENAI_MODEL,
    extra_headers: {},
    base_url: undefined,
    response_format: undefined,
    seed: undefined,
    reasoning_effort: undefined,
    openai_api_base: undefined,
  },
  'langsmith/playground/CustomModelEndpoint': {
    temperature: 1.0,
    max_tokens: undefined,
    top_p: 1.0,
    presence_penalty: null,
    frequency_penalty: null,
    model: DEFAULT_OPENAI_MODEL,
    extra_headers: {},
    base_url: undefined,
    openai_api_base: undefined,
  },
  'langchain/chat_models/anthropic/ChatAnthropic': {
    temperature: 1.0,
    max_tokens: 1024,
    top_p: null,
    top_k: null,
    model: DEFAULT_ANTHROPIC_MODEL,
  },
  'langchain/llms/googlellm/VertexAI': {
    temperature: 0.2,
    max_output_tokens: 1024,
    top_k: 40,
    top_p: -1,
    model: DEFAULT_VERTEX_AI_MODEL,
  },
  'langchain/chat_models/google_genai/ChatGoogleGenerativeAI': {
    temperature: 1,
    top_p: 1,
    max_tokens: undefined,
    model: DEFAULT_GOOGLE_GENAI_MODEL,
  },
  'langchain/chat_models/mistral_ai/ChatMistralAI': {
    temperature: 0.7,
    top_p: 1,
    max_tokens: undefined,
    model: DEFAULT_CHAT_MISTRAL_AI_MODEL,
  },
  'langchain/chat_models/chat_integration/ChatVertexAI': {
    temperature: 1,
    max_tokens: 1024,
    top_k: 40,
    top_p: 0.95,
    model: DEFAULT_CHAT_VERTEX_AI_MODEL,
  },
  'langchain/llms/fireworks/Fireworks': {
    temperature: 0.2,
    max_tokens: 1024,
    top_p: -1,
    model: undefined,
  },
  'langchain/chat_models/fireworks/ChatFireworks': {
    temperature: 0.2,
    max_tokens: 1024,
    top_p: 1,
    model: undefined,
  },
  'langchain/llms/bedrock/Bedrock': {
    temperature: 1,
    max_tokens: undefined,
    model_id: DEFAULT_BEDROCK_MODEL,
    region_name: undefined,
  },
  'langchain/chat_models/bedrock/BedrockChat': {
    temperature: 1,
    model_id: DEFAULT_BEDROCK_CHAT_MODEL,
    max_tokens: undefined,
    region_name: undefined,
  },
  'langsmith/playground/ChatCustomModel': {
    url: 'https://example.com',
    configurable: {},
  },
  'langsmith/playground/CustomModel': {
    url: 'https://example.com',
    configurable: {},
  },
  'langchain/chat_models/groq/ChatGroq': {
    temperature: 0.7,
    max_tokens: undefined,
    model: DEFAULT_CHAT_GROQ_MODEL,
  },
  'langchain/chat_models/vertexai/ChatVertexAI': {
    temperature: 1,
    max_tokens: 1024,
    top_k: 40,
    top_p: 0.95,
    model: DEFAULT_CHAT_VERTEX_AI_MODEL,
  },
  'langchain/chat_models/mistralai/ChatMistralAI': {
    temperature: 0.7,
    top_p: 1,
    max_tokens: undefined,
    model: DEFAULT_CHAT_MISTRAL_AI_MODEL,
  },
  'langchain_google_genai/chat_models/ChatGoogleGenerativeAI': {
    temperature: 1,
    top_p: 1,
    model: DEFAULT_GOOGLE_GENAI_MODEL,
    max_tokens: undefined,
  },
  'langchain/chat_models/bedrock/ChatBedrock': {
    temperature: 1,
    model_id: DEFAULT_BEDROCK_CHAT_MODEL,
    max_tokens: undefined,
    region_name: undefined,
  },
  'langchain_aws/chat_models/ChatBedrockConverse': {
    temperature: 1,
    model_id: DEFAULT_BEDROCK_CHAT_MODEL,
    max_tokens: undefined,
    region_name: undefined,
  },
  'langchain_groq/chat_models/ChatGroq': {
    temperature: 0.7,
    max_tokens: undefined,
    model: DEFAULT_CHAT_GROQ_MODEL,
  },
  'langchain_xai/chat_models/ChatXAI': {
    temperature: 1.0,
    max_tokens: null,
    top_p: 1.0,
    presence_penalty: 0.0,
    frequency_penalty: 0.0,
    model: DEFAULT_CHAT_XAI_MODEL,
  },
  'langchain_deepseek/chat_models/ChatDeepSeek': {
    temperature: 1,
    max_tokens: 4000,
    top_p: 1.0,
    presence_penalty: 0.0,
    frequency_penalty: 0.0,
    model: DEFAULT_CHAT_DEEPSEEK_MODEL,
  },
};

const modelsForPlayground = (
  chatModelMapping: Record<string, { modelId: string; displayName: string }>
) => {
  const modelIds = Object.values(chatModelMapping).map(
    (model) => model.modelId
  );
  return [...new Set(modelIds)];
};

export const CHAT_MODELS_FOR_PLAYGROUND =
  modelsForPlayground(CHAT_MODEL_MAPPING);

export const TEXT_MODELS_FOR_PLAYGROUND =
  modelsForPlayground(TEXT_MODEL_MAPPING);

export const SUPPORTED_RUNNABLES = [
  {
    category: 'Chat Models',
    items: CHAT_MODELS_FOR_PLAYGROUND,
  },
  {
    category: 'LLMs',
    items: TEXT_MODELS_FOR_PLAYGROUND,
  },
  {
    category: 'Output Parsers',
    items: [
      'langchain/output_parsers/list/CommaSeparatedListOutputParser',
      'langchain/output_parsers/openai_functions/JsonOutputFunctionsParser',
      'langchain/output_parsers/openai_functions/JsonKeyOutputFunctionsParser',
      'langchain/output_parsers/openai_tools/JsonOutputToolsParser',
      'langchain/schema/output_parser/StrOutputParser',
      'langchain/output_parsers/regex/RegexParser',
    ],
    hiddenItems: [
      'langchain_core/output_parsers/list/CommaSeparatedListOutputParser',
      'langchain_core/output_parsers/string/StrOutputParser',
    ],
  },
  {
    category: 'Prompts',
    items: [
      'langchain/prompts/prompt/PromptTemplate',
      'langchain/prompts/chat/ChatPromptTemplate',
      'langchain_core/prompts/structured/StructuredPrompt',
    ],
    hiddenItems: [
      'langchain_core/prompts/prompt/PromptTemplate',
      'langchain_core/prompts/chat/ChatPromptTemplate',
    ],
  },
  {
    category: 'Structure',
    items: [
      'langchain/schema/runnable/RunnableSequence',
      'langchain/schema/runnable/RunnableMap',
    ],
    hiddenItems: [
      'langchain_core/runnables/RunnableSequence',
      'langchain_core/runnables/RunnableMap',
    ],
  },
];

export const DEFAULT_CHAT_MODEL_CONSTRUCTOR: SerializedConstructor = {
  lc: 1,
  type: 'constructor',
  id: DEFAULT_CHAT_MODEL?.modelId.split('/'),
  kwargs: MODEL_DEFAULT_KWARGS[DEFAULT_CHAT_MODEL.modelId],
};

export const BUILTIN_TOOLS: Record<
  string,
  {
    displayName: string;
    description: string;
    docsLink: string;
    tool: BuiltinTool;
    disableStreaming?: boolean;
  }[]
> = {
  [CHAT_MODEL_MAPPING.ChatOpenAI.modelId]: [
    {
      displayName: 'Web Search',
      description: 'This tool allows the model to search the web in real time.',
      docsLink:
        'https://platform.openai.com/docs/guides/tools-web-search?api-mode=responses',
      tool: {
        type: 'web_search_preview',
      },
    },
    {
      displayName: 'Image Generation',
      description: 'This tool allows the model to generate images.',
      docsLink:
        'https://platform.openai.com/docs/guides/tools-image-generation',
      tool: {
        type: 'image_generation',
      },
      disableStreaming: true,
    },
    {
      displayName: 'MCP',
      description:
        'This tool gives the model access to tools hosted on a remote MCP server.',
      docsLink: 'https://platform.openai.com/docs/guides/tools-remote-mcp',
      tool: {
        type: 'mcp',
        server_label: '',
        server_url: '',
      },
    },
  ],
  [CHAT_MODEL_MAPPING.ChatAnthropic.modelId]: [
    {
      displayName: 'Web Search',
      description: 'This tool allows the model to search the web in real time.',
      docsLink:
        'https://docs.anthropic.com/en/docs/agents-and-tools/tool-use/web-search-tool',
      tool: {
        type: 'web_search_20250305',
        name: 'web_search',
      },
    },
  ],
};

export const BUILTIN_TOOLS_DOCS_LINKS = {
  [CHAT_MODEL_MAPPING.ChatOpenAI.modelId]:
    'https://python.langchain.com/docs/integrations/chat/openai/#built-in-tools',
  [CHAT_MODEL_MAPPING.ChatAnthropic.modelId]:
    'https://python.langchain.com/docs/integrations/chat/anthropic/#built-in-tools',
};

export const BUILTIN_TOOL_TYPES_THAT_DISABLE_STREAMING = Object.values(
  BUILTIN_TOOLS
)
  .flatMap((tools) =>
    tools.map((t) => ({
      type: t.tool.type,
      disableStreaming: t.disableStreaming,
    }))
  )
  .filter((t) => t.disableStreaming)
  .map((t) => t.type);
